import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import Button from '../common/Button';
import { getPostComments } from '../../api/comments';

/**
 * CommentList component to display comments for a post
 * @param {Object} props - Component props
 * @param {string} props.postId - Post ID
 * @param {boolean} props.allowComments - Whether to allow adding comments
 * @param {Array} props.initialComments - Optional initial comments to display
 * @returns {JSX.Element} CommentList component
 */
const CommentList = ({ postId, allowComments, initialComments = [] }) => {
  const [comments, setComments] = useState(initialComments || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Fetch comments if no initial comments provided
  useEffect(() => {
    // If we have initial comments, use those
    if (initialComments && initialComments.length > 0) {
      setComments(initialComments);
      return;
    }
    
    // Otherwise fetch comments
    const fetchComments = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await getPostComments(postId, { page: 1 });
        setComments(response.data.data.comments);
        
        // Check if there are more comments to load
        const pagination = response.data.pagination;
        setHasMore(pagination.page < pagination.pages);
      } catch (error) {
        console.error('Error fetching comments:', error);
        setError('Failed to load comments');
      } finally {
        setLoading(false);
      }
    };
    
    fetchComments();
  }, [postId, initialComments]);
  
  // Load more comments
  const loadMoreComments = async () => {
    try {
      setLoading(true);
      setError('');
      
      const nextPage = page + 1;
      const response = await getPostComments(postId, { page: nextPage });
      
      // Append new comments to existing ones
      setComments(prevComments => [
        ...prevComments,
        ...response.data.data.comments
      ]);
      
      // Update pagination state
      setPage(nextPage);
      
      const pagination = response.data.pagination;
      setHasMore(pagination.page < pagination.pages);
    } catch (error) {
      console.error('Error loading more comments:', error);
      setError('Failed to load more comments');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle new comment added
  const handleCommentAdded = (newComment) => {
    setComments(prevComments => [newComment, ...prevComments]);
  };
  
  // Handle comment updated
  const handleCommentUpdated = (updatedComment) => {
    setComments(prevComments =>
      prevComments.map(comment =>
        comment._id === updatedComment._id ? updatedComment : comment
      )
    );
  };
  
  // Handle comment deleted
  const handleCommentDeleted = (commentId) => {
    setComments(prevComments =>
      prevComments.filter(comment => comment._id !== commentId)
    );
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Comments ({comments ? comments.length : 0})
      </h3>
      
      {/* Comment form */}
      {allowComments && (
        <div className="mb-6">
          <CommentForm
            postId={postId}
            onSuccess={handleCommentAdded}
          />
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}
      
      {/* Comments list */}
      {comments && comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map(comment => (
            <CommentItem
              key={comment._id}
              comment={comment}
              postId={postId}
              onUpdate={handleCommentUpdated}
              onDelete={handleCommentDeleted}
            />
          ))}
          
          {/* Load more button */}
          {hasMore && (
            <div className="text-center pt-2">
              <Button
                variant="light"
                onClick={loadMoreComments}
                isLoading={loading}
                isDisabled={loading}
              >
                Load More Comments
              </Button>
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="text-center py-4">
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 rounded-full border-t-transparent mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading comments...</p>
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">
          No comments yet. Be the first to comment!
        </div>
      )}
    </div>
  );
};

CommentList.propTypes = {
  postId: PropTypes.string.isRequired,
  allowComments: PropTypes.bool,
  initialComments: PropTypes.array
};

export default CommentList;